/**
 * TreeTable - Hierarchical data display
 *
 * Usage:
 *   <TreeTable
 *     title="Expense Categories"
 *     data={{
 *       nodes: [
 *         {
 *           id: '1',
 *           label: 'Living',
 *           value: 2500,
 *           icon: '🏠',
 *           children: [
 *             { id: '1a', label: 'Rent', value: 1800 },
 *             { id: '1b', label: 'Utilities', value: 200 },
 *             { id: '1c', label: 'Internet', value: 80 },
 *           ]
 *         },
 *         { id: '2', label: 'Food', value: 600, icon: '🍔' },
 *       ],
 *       valueLabel: 'Amount'
 *     }}
 *   />
 */

import { useState } from 'react';
import type { BlockProps, TreeTableData, TreeNode } from './types';
import './blocks.css';

export interface TreeTableProps extends BlockProps<TreeTableData> {
  /** Initially expand all nodes */
  expandAll?: boolean;
  /** Value formatter */
  formatValue?: (value: string | number) => string;
}

export function TreeTable({
  data,
  title,
  expandAll = false,
  formatValue,
  className,
}: TreeTableProps) {
  const { nodes, valueLabel = 'Value' } = data;

  // Track expanded state for each node
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(() => {
    if (expandAll) {
      const allIds = new Set<string>();
      const collectIds = (nodes: TreeNode[]) => {
        nodes.forEach(node => {
          if (node.children?.length) {
            allIds.add(node.id);
            collectIds(node.children);
          }
        });
      };
      collectIds(nodes);
      return allIds;
    }
    // By default, expand nodes that have expanded: true
    const initialExpanded = new Set<string>();
    const collectExpanded = (nodes: TreeNode[]) => {
      nodes.forEach(node => {
        if (node.expanded && node.children?.length) {
          initialExpanded.add(node.id);
        }
        if (node.children) {
          collectExpanded(node.children);
        }
      });
    };
    collectExpanded(nodes);
    return initialExpanded;
  });

  const toggleExpanded = (nodeId: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const defaultFormatValue = (value: string | number): string => {
    if (typeof value === 'number') {
      return value.toLocaleString();
    }
    return String(value);
  };

  const formatter = formatValue || defaultFormatValue;

  if (!nodes || nodes.length === 0) {
    return (
      <div className={`block-table tree-table ${className || ''}`}>
        {title && <div className="block-table-title">{title}</div>}
        <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--color-text-faint)' }}>
          No data available
        </div>
      </div>
    );
  }

  return (
    <div className={`block-table tree-table ${className || ''}`}>
      {title && <div className="block-table-title">{title}</div>}

      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Name</th>
              <th style={{ textAlign: 'right', width: '120px' }}>{valueLabel}</th>
            </tr>
          </thead>
          <tbody>
            {nodes.map(node => (
              <TreeRow
                key={node.id}
                node={node}
                depth={0}
                expandedNodes={expandedNodes}
                toggleExpanded={toggleExpanded}
                formatValue={formatter}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TreeRow({
  node,
  depth,
  expandedNodes,
  toggleExpanded,
  formatValue,
}: {
  node: TreeNode;
  depth: number;
  expandedNodes: Set<string>;
  toggleExpanded: (id: string) => void;
  formatValue: (value: string | number) => string;
}) {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedNodes.has(node.id);

  return (
    <>
      <tr style={{ background: depth > 0 ? 'var(--color-surface-muted)' : undefined }}>
        <td style={{ paddingLeft: `${16 + depth * 20}px` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {hasChildren ? (
              <button
                onClick={() => toggleExpanded(node.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleExpanded(node.id);
                  }
                }}
                style={{
                  width: '20px',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 'none',
                  background: 'var(--color-surface-muted)',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '10px',
                  color: 'var(--color-text-muted)',
                  transition: 'transform 0.15s ease',
                  transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                }}
                aria-expanded={isExpanded}
                aria-label={isExpanded ? `Collapse ${node.label}` : `Expand ${node.label}`}
              >
                ▶
              </button>
            ) : (
              <span style={{ width: '20px' }} />
            )}

            {node.icon && (
              <span style={{ fontSize: '14px' }}>{node.icon}</span>
            )}

            <span style={{
              fontWeight: hasChildren ? 600 : 400,
              color: 'var(--color-text)',
            }}>
              {node.label}
            </span>

            {hasChildren && (
              <span style={{
                fontSize: '11px',
                color: 'var(--color-text-muted)',
                background: 'var(--color-surface-muted)',
                padding: '1px 6px',
                borderRadius: '8px',
              }}>
                {node.children!.length}
              </span>
            )}
          </div>
        </td>
        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
          {node.value !== undefined ? (
            <span style={{
              fontWeight: hasChildren ? 600 : 400,
              color: hasChildren ? 'var(--color-text)' : 'var(--color-text-subtle)',
            }}>
              {formatValue(node.value)}
            </span>
          ) : (
            <span style={{ color: 'var(--color-text-muted)' }}>—</span>
          )}
        </td>
      </tr>

      {hasChildren && isExpanded && node.children!.map(child => (
        <TreeRow
          key={child.id}
          node={child}
          depth={depth + 1}
          expandedNodes={expandedNodes}
          toggleExpanded={toggleExpanded}
          formatValue={formatValue}
        />
      ))}
    </>
  );
}

export default TreeTable;
